import React, { useState, useRef } from 'react';
import { type ApolloQueryResult } from '@apollo/client';
import { ForwardToInbox, PersonAddAlt1 } from '@mui/icons-material';
import { Typography } from '@mui/material';
import { SUPERVISOR_MONITOR_TEXT } from '../../lang';
import { nonProposedFilter } from '../../filters';
import { useInviteNonProposedStudent } from '../../../graphql';
import {
  Alert,
  type AlertDetails,
  type DialogDetails,
  Dialog,
} from '../../../../../components';
import { useAuth } from '../../../../../contexts';
import {
  ApplicationViewList,
  type ApplicationViewListProps,
  type ApplicationViewTableDataItem,
  type CustomAction,
  type FilterFields,
  type TableColumns,
} from '../../../../Application/components';
import { COMMON_ACTION } from '../../../../Application/components/ApplicationViewList/constants';

const SUPERVISOR_LIST = SUPERVISOR_MONITOR_TEXT.SUPERVISOR_LIST;

export const SupervisorNonProposedList: React.FunctionComponent = () => {
  const { user } = useAuth();
  // default value
  const getRefetchRef = useRef<() => Promise<ApolloQueryResult<any>>>(
    async () => ({
      data: {},
      loading: false,
      networkStatus: 7,
      stale: false,
    }),
  );

  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'error',
  });
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [dialogDetails, setDialogDetails] = useState<DialogDetails>({
    title: '',
  });

  const inviteNonProposedStudent = useInviteNonProposedStudent();
  const handleInviteNonProposedStudent = async (
    applicationId: string,
  ): Promise<void> => {
    setDialogOpen(false);

    try {
      await inviteNonProposedStudent({
        variables: {
          applicationId,
        },
      });

      setAlertDetails({
        alertContent: SUPERVISOR_LIST.SEND_INVITATION_SUCCESS_ALERT,
        alertType: 'success',
      });
      await getRefetchRef.current();
    } catch (error) {
      setAlertDetails({
        alertContent: 'Error Occurs.' + (error as Error)?.message,
        alertType: 'warning',
      });
    } finally {
      setOpenAlert(true);
    }
  };

  const handlePrepareInvitation = (
    row: ApplicationViewTableDataItem,
    refetch: () => Promise<ApolloQueryResult<any>>,
  ): void => {
    setDialogOpen(true);
    setDialogDetails({
      title: SUPERVISOR_LIST.SEND_INVITATION_TITLE,
      content: SUPERVISOR_LIST.SEND_INVITATION_CONFIRMATION,
      confirmAction: 'Yes',
      cancelAction: 'No',
      handleDialogConfirmAction: async () => {
        await handleInviteNonProposedStudent(row.applicationId);
      },
    });

    // Ref is acceptable here. Because currently update ref is triggered together with setState, the re-render must be triggered.
    // But as update ref.current will not trigger re-render itself,
    // there is a risk (if remove setDialogControl here) that after refetch updated, the latest value is not passed to component.
    getRefetchRef.current = refetch;
  };

  const checkIfSentInvitation = (
    row: ApplicationViewTableDataItem,
  ): boolean => {
    return row.supervisorInvitations?.some(
      el => el.key === user?.userInfo.netId,
    );
  };

  const customActions: CustomAction[] = [
    {
      icon: row => {
        return checkIfSentInvitation(row) ? (
          <ForwardToInbox />
        ) : (
          <PersonAddAlt1 />
        );
      },
      handleAction: handlePrepareInvitation,
      checkDisable: row => {
        return checkIfSentInvitation(row);
      },
    },
  ];

  const hiddenFilters: FilterFields[] = React.useMemo(
    () => [
      'department',
      'applicantLocalIndicator',
      'scholarshipAppliedByApplicant',
      'applicationStatus',
      'fundingSchemeStatus',
      'fundingScheme',
      'hkpfsNo',
      'dualPartnerUniversity',
      'jointpartnerUniversity',
      'partnerCategory',
      'gsAssignee',
    ],
    [],
  );

  const visibleColumns: TableColumns[] = React.useMemo(
    () => [
      'applicationNo',
      'applicantName',
      'applicantEmail',
      'department',
      'applicantNationality',
      'entryYear',
      'applyProgramme',
      'researchAreas',
      'nominatedFundings',
      'applicationStatus',
      'applicationSubmissionTime',
    ],
    [],
  );

  const filterProps: ApplicationViewListProps['filterProps'] = {
    hiddenFilterFields: hiddenFilters,
  };

  const toolsProps: ApplicationViewListProps['toolsProps'] = {
    enableExport: false,
  };

  const tableProps: ApplicationViewListProps['tableProps'] = {
    initialVisibleColumns: visibleColumns,
    pickCommonActions: [COMMON_ACTION.MONITOR],
    listFilter: nonProposedFilter(user),
    customActions,
  };
  return (
    <div>
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
      <ApplicationViewList
        tableProps={tableProps}
        filterProps={filterProps}
        toolsProps={toolsProps}
      />
      <Dialog
        openDialog={dialogOpen}
        dialogTitle={dialogDetails.title}
        dialogAction={dialogDetails.confirmAction}
        cancelAction={dialogDetails.cancelAction ?? undefined}
        onDialogAction={dialogDetails.handleDialogConfirmAction}
        displayCloseButton={true}
        width="800px"
        onDialogClose={() => {
          setDialogOpen(false);
        }}
      >
        <Typography margin={'0rem 1.5rem 2rem'}>
          {dialogDetails.content}
        </Typography>
      </Dialog>
    </div>
  );
};
